import { z } from 'zod';

/**
 * Validation schemas for all forms in the application.
 * Uses Zod for runtime validation and type safety.
 */

// Admin Login Schema
export const AdminLoginSchema = z.object({
  username: z.string()
    .min(1, 'Username is required')
    .min(3, 'Username must be at least 3 characters')
    .max(50, 'Username must be less than 50 characters')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, underscores, and hyphens'),
  password: z.string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters')
});

// Tenant Schema
export const TenantSchema = z.object({
  name: z.string()
    .min(1, 'Tenant name is required')
    .max(100, 'Tenant name must be less than 100 characters'),
  domain: z.string()
    .min(1, 'Domain is required')
    .regex(/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/, 'Invalid domain format'),
  clientId: z.string()
    .min(1, 'Client ID is required')
    .max(500, 'Client ID is too long'),
  tenantId: z.string()
    .min(1, 'Tenant ID is required')
    .max(500, 'Tenant ID is too long'),
  authority: z.string()
    .min(1, 'Authority is required')
    .url('Authority must be a valid URL'),
  allowPasswordReset: z.boolean().default(true),
  allowAccountUnlock: z.boolean().default(true)
});

// Admin User Schema
export const AdminUserSchema = z.object({
  username: z.string()
    .min(1, 'Username is required')
    .min(3, 'Username must be at least 3 characters')
    .max(50, 'Username must be less than 50 characters')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, underscores, and hyphens'),
  email: z.string()
    .min(1, 'Email is required')
    .email('Invalid email format'),
  password: z.string()
    .min(1, 'Password is required')
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  role: z.enum(['admin', 'super_admin']).default('admin')
});

// Password Change Schema
export const PasswordChangeSchema = z.object({
  currentPassword: z.string()
    .min(1, 'Current password is required'),
  newPassword: z.string()
    .min(1, 'New password is required')
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
});

// Chat Domain Schema
export const ChatDomainSchema = z.object({
  domain: z.string()
    .min(1, 'Domain is required')
    .regex(/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/, 'Invalid domain format')
});

// Chat Email Schema
export const ChatEmailSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .email('Invalid email format')
});

// Chat Verification Code Schema
export const ChatVerificationSchema = z.object({
  code: z.string()
    .min(1, 'Verification code is required')
    .length(6, 'Verification code must be 6 digits')
    .regex(/^[0-9]+$/, 'Verification code must contain only numbers')
});

// Chat Message Schema
export const ChatMessageSchema = z.object({
  message: z.string()
    .min(1, 'Message cannot be empty')
    .max(5000, 'Message is too long')
});

/**
 * Validate data against a schema.
 * 
 * Args:
 *   schema: Zod schema to validate against
 *   data: Data to validate
 * 
 * Returns:
 *   Object with { isValid, data, error }
 */
export const validateData = (schema, data) => {
  try {
    const validatedData = schema.parse(data);
    return { isValid: true, data: validatedData, error: null };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessage = error.errors[0]?.message || 'Validation failed';
      return { isValid: false, data: null, error: errorMessage };
    }
    return { isValid: false, data: null, error: 'Unknown validation error' };
  }
};

